import {
  ScrollableCurriculumTabStrip,
  type ScrollableCurriculumTabStripHandle,
} from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { CurriculumParentStripNavSyncContext } from "@/contexts/CurriculumParentStripNavSyncContext";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BeltRank } from "../../MembersArea";
import AhmHak from "./AhmHak";
import BalSaeDae from "./BalSaeDae";
import BalSeakSo from "./BalSeakSo";
import ChulGi from "./ChulGi";
import JaEun from "./JaEun";
import KeumKangKwon from "./KeumKangKwon";
import Rohai from "./Rohai";
import SoHoYeon from "./SoHoYeon";
import YeonBi from "./YeonBi";

type Section = { id: string; label: string };

const DEFAULT_SURFACE = "!bg-gray-200 !text-gray-900";

export interface CurriculumSongMooKwanProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  beltTabSurfaceClass: string;
  hasDemo: (aliases: string[]) => boolean;
  onWatchDemo: (aliases: string[]) => void;
  showChulGi: boolean;
  showRohai: boolean;
  showBalSaeDae: boolean;
  showBalSeakSo: boolean;
  showYeonBi: boolean;
  showAhmHak: boolean;
  showSoHoYeon: boolean;
  showJaEun: boolean;
  showKeumKangKwon: boolean;
}

export default function CurriculumSongMooKwan({
  selectedBelt,
  getBeltColor,
  beltTabSurfaceClass,
  hasDemo,
  onWatchDemo,
  showChulGi,
  showRohai,
  showBalSaeDae,
  showBalSeakSo,
  showYeonBi,
  showAhmHak,
  showSoHoYeon,
  showJaEun,
  showKeumKangKwon,
}: CurriculumSongMooKwanProps) {
  const sections: Section[] = useMemo(() => {
    const rows: Section[] = [];
    if (showChulGi) rows.push({ id: "chulgi", label: "ChulGi" });
    if (showRohai) rows.push({ id: "rohai", label: "Rohai" });
    if (showBalSaeDae) rows.push({ id: "balsaekdae", label: "BalSaek Dae" });
    if (showBalSeakSo) rows.push({ id: "balseakso", label: "BalSaek So" });
    if (showYeonBi) rows.push({ id: "yeonbi", label: "YeonBi" });
    if (showAhmHak) rows.push({ id: "ahmhak", label: "AhmHak" });
    if (showSoHoYeon) rows.push({ id: "sohoyeon", label: "SoHoYeon" });
    if (showJaEun) rows.push({ id: "jaeun", label: "JaEun" });
    if (showKeumKangKwon) rows.push({ id: "keumkangkwon", label: "KeumKangKwon" });
    return rows;
  }, [
    showChulGi,
    showRohai,
    showBalSaeDae,
    showBalSeakSo,
    showYeonBi,
    showAhmHak,
    showSoHoYeon,
    showJaEun,
    showKeumKangKwon,
  ]);

  const fingerprint = sections.map((s) => s.id).join("|");
  const [mainTab, setMainTab] = useState(() => sections[0]?.id ?? "chulgi");

  useEffect(() => {
    if (!sections.some((s) => s.id === mainTab)) {
      setMainTab(sections[0]?.id ?? "");
    }
  }, [fingerprint, mainTab, sections]);

  const surface = beltTabSurfaceClass ?? DEFAULT_SURFACE;
  const smkStripRef = useRef<ScrollableCurriculumTabStripHandle>(null);
  const scrollSongMooKwanOrchestratorStrip = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      smkStripRef.current?.getScrollContainer() ?? null,
    );
  }, []);

  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium transition-shadow",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  if (sections.length === 0) return null;

  return (
    <div className="space-y-4 text-left">
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full space-y-4">
        <ScrollableCurriculumTabStrip ref={smkStripRef} key={fingerprint} ariaLabelPrefix="Song Moo Kwan">
          {sections.map((s) => (
            <TabsTrigger key={s.id} value={s.id} className={triggerClass}>
              {s.label}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        <CurriculumParentStripNavSyncContext.Provider value={scrollSongMooKwanOrchestratorStrip}>
        {showChulGi && (
          <TabsContent value="chulgi" className="mt-0 focus-visible:outline-none">
            <ChulGi
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showRohai && (
          <TabsContent value="rohai" className="mt-0 focus-visible:outline-none">
            <Rohai
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showBalSaeDae && (
          <TabsContent value="balsaekdae" className="mt-0 focus-visible:outline-none">
            <BalSaeDae
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showBalSeakSo && (
          <TabsContent value="balseakso" className="mt-0 focus-visible:outline-none">
            <BalSeakSo
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showYeonBi && (
          <TabsContent value="yeonbi" className="mt-0 focus-visible:outline-none">
            <YeonBi
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showAhmHak && (
          <TabsContent value="ahmhak" className="mt-0 focus-visible:outline-none">
            <AhmHak
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showSoHoYeon && (
          <TabsContent value="sohoyeon" className="mt-0 focus-visible:outline-none">
            <SoHoYeon
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showJaEun && (
          <TabsContent value="jaeun" className="mt-0 focus-visible:outline-none">
            <JaEun
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showKeumKangKwon && (
          <TabsContent value="keumkangkwon" className="mt-0 focus-visible:outline-none">
            <KeumKangKwon
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}
        </CurriculumParentStripNavSyncContext.Provider>
      </Tabs>
    </div>
  );
}
